import { Link } from 'react-router-dom';
import { useState, Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

/* Small, consistent inline-SVG icon set (no icon-library dependency). */
function IconMark({ name }: { name: string }) {
  const svg = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const glyph: Record<string, any> = {
    percent:  <><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></>,
    ticket:   <><rect x="3" y="7" width="18" height="10" rx="2" /><path d="M9 7v10" /></>,
    bolt:     <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />,
    verified: <><path d="M12 3 5 6v6c0 4 3 6.6 7 8 4-1.4 7-4 7-8V6l-7-3Z" /><path d="m9 12 2 2 4-4" /></>,
    support:  <><path d="M4 14v-2a8 8 0 0 1 16 0v2" /><rect x="2" y="14" width="4" height="6" rx="1.5" /><rect x="18" y="14" width="4" height="6" rx="1.5" /></>,
  };
  return (
    <span className="inline-grid place-items-center w-11 h-11 rounded-xl bg-gold/12 text-gold-dark border border-gold/25 mb-4">
      <svg {...svg}>{glyph[name] ?? glyph.percent}</svg>
    </span>
  );
}

/**
 * Membership call-to-action that adapts to the visitor:
 *  - logged-out          → register with the member plan pre-selected
 *  - logged-in, no plan  → start Stripe Checkout directly (no new account)
 *  - active member       → link to the dashboard to manage/cancel
 */
function JoinCTA({ plan = 'monthly', className, label }: { plan?: 'monthly' | 'annual'; className: string; label: string }) {
  const user = useAuthStore((s) => s.user);
  const [busy, setBusy] = useState(false);

  const { data: membership, refetch } = useQuery<{ tier: string }>({
    queryKey: ['membership'],
    queryFn: async () => (await api.get('/membership')).data,
    enabled: !!user,
    retry: false,
  });
  const isMember = membership?.tier === 'MEMBER';

  if (user && isMember) {
    return <Link to="/dashboard/membership" className={className}>Manage membership &rarr;</Link>;
  }

  if (user) {
    const start = async () => {
      setBusy(true);
      try {
        const { data } = await api.post('/membership/checkout', { plan });
        if (data?.url) { window.location.href = data.url; return; }
        if (data?.demo || data?.activated) {
          toast.success('Welcome to Souqli Member! 🎉 Fee-free on up to 12 orders / £300 a month.');
          await refetch();
          return;
        }
        toast.error('Could not start checkout.');
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? 'Membership checkout is unavailable.');
      } finally {
        setBusy(false);
      }
    };
    return (
      <button onClick={start} disabled={busy} className={className}>
        {busy ? 'Opening checkout…' : label}
      </button>
    );
  }

  return <Link to="/register?plan=member" className={className}>{label}</Link>;
}

/* Guest-vs-Member comparison. Booleans render as ✓ / -, strings render verbatim. */
const compareRows: { label: string; guest: string | boolean; member: string | boolean }[] = [
  { label: 'Buyer protection fee',        guest: '4.5% + 70p',   member: 'Free · then 3.5% + 60p' },
  { label: 'Fee-free orders / month',     guest: false,  member: '12' },
  { label: 'Fee-free spend / month',      guest: false,  member: '£300' },
  { label: 'Protected payments',   guest: true,   member: true },
  { label: 'ID-verified sellers',         guest: true,   member: true },
  { label: '£4 delivery voucher (spend £300+)', guest: false, member: 'monthly' },
  { label: 'Early access to new drops',   guest: false,  member: '1 hour early' },
  { label: 'Verified Buyer badge',        guest: false,  member: true },
  { label: 'Priority human support',      guest: false,  member: true },
];

/** Render a comparison cell - tick / dash for booleans, text otherwise. */
function cmpCell(v: string | boolean, member: boolean) {
  if (typeof v === 'boolean') {
    return v
      ? <span className={member ? 'text-gold-dark' : 'text-sage'}>✓</span>
      : <span className="text-mute/40">-</span>;
  }
  return <span className={member ? 'text-gold-dark font-semibold' : 'text-mute'}>{v}</span>;
}

const perks = [
  { icon: 'percent', title: 'Fee-free shopping',       body: 'Guests pay 4.5% + 70p on every purchase. Members pay nothing on up to 12 orders and £300 of spend each month, then just 3.5% + 60p beyond that.' },
  { icon: 'ticket', title: '£4 delivery voucher',     body: 'Spend over £300 in a month and earn a £4 voucher for your next order’s delivery. Redeem it whenever you like; it lasts three months.' },
  { icon: 'bolt',     title: 'Early access to drops', body: 'See newly listed pieces a full hour before everyone else. The best pieces go fast, and members get first pick.' },
  { icon: 'verified',     title: 'Verified buyer badge',  body: 'Your profile displays a Verified Member badge. Sellers know you are a trusted buyer.' },
  { icon: 'support', title: 'Priority support',      body: 'Skip the queue. Member disputes are handled first, by a real person rather than a bot.' },
];

function BreakevenCalc() {
  const [spend, setSpend] = useState(180);
  const [orders, setOrders] = useState(6);
  const MEMBERSHIP = 10.99;

  // Guest: 4.5% of spend + 70p on every order.
  const guestCost = spend * 0.045 + orders * 0.70;

  // Member: fee-free on the first 12 orders AND first £300 of spend each month;
  // beyond either, 3.5% on the spend over £300 and 60p on the orders over 12.
  const excessSpend  = Math.max(0, spend - 300);
  const excessOrders = Math.max(0, orders - 12);
  const memberFees   = excessSpend * 0.035 + excessOrders * 0.60;

  // Spending £300+ in a month also earns a £4 delivery voucher — a real benefit,
  // so it is credited against the membership cost (optional, expires in 3 months).
  const voucher     = spend >= 300 ? 4 : 0;
  const memberCost  = memberFees + MEMBERSHIP - voucher;

  const net   = guestCost - memberCost; // positive => membership is cheaper
  const worth = net > 0;
  const beSpend = (MEMBERSHIP - orders * 0.70) / 0.045;
  const beText = orders * 0.70 >= MEMBERSHIP
    ? `At ${orders} orders a month, membership pays for itself on the per-order fees alone.`
    : `At ${orders} orders a month, membership pays off from about £${Math.max(0, Math.ceil(beSpend / 5) * 5)} spend a month.`;
  const money = (n: number) => `£${Math.abs(n).toFixed(2)}`;

  return (
    <div className="bg-white border border-gold/30 rounded-luxe p-6 md:p-8 shadow-luxe">
      <h3 className="display text-lg text-ink mb-1">Does membership pay for itself?</h3>

      <div className="mb-5">
        <div className="flex justify-between text-xs text-mute mb-1"><span>Monthly spend</span><span className="text-gold-dark font-semibold">£{spend}</span></div>
        <input type="range" min={10} max={500} step={5} value={spend} onChange={e => setSpend(Number(e.target.value))} className="w-full accent-gold" />
      </div>
      <div className="mb-6">
        <div className="flex justify-between text-xs text-mute mb-1"><span>Orders a month</span><span className="text-gold-dark font-semibold">{orders}</span></div>
        <input type="range" min={1} max={20} step={1} value={orders} onChange={e => setOrders(Number(e.target.value))} className="w-full accent-gold" />
      </div>

      <div className="grid grid-cols-2 gap-3 text-center mb-4">
        <div className="bg-cream-deep rounded-lg p-3 border border-warmline">
          <p className="text-[10px] text-mute uppercase tracking-wide mb-1">Guest — fees</p>
          <p className="display text-xl text-ink/70">{money(guestCost)}</p>
        </div>
        <div className="bg-gold/10 border border-gold/30 rounded-lg p-3">
          <p className="text-[10px] text-gold-dark uppercase tracking-wide mb-1">Member — fees + £10.99{voucher ? ' − £4' : ''}</p>
          <p className="display text-xl text-gold-dark">{money(memberCost)}</p>
        </div>
      </div>

      <div className={`rounded-lg p-4 text-center ${worth ? 'bg-sage/20 border border-sage/40' : 'bg-cream-deep border border-warmline'}`}>
        {worth ? (
          <p className="text-sage text-sm font-medium">✓ At this usage, membership saves you {money(net)} a month on fees.</p>
        ) : (
          <p className="text-ink/75 text-sm font-medium">At this usage you’d pay {money(net)} more as a member — on fees alone you’re better off as a guest.</p>
        )}
      </div>

      <p className="text-center text-mute text-[11px] mt-3">{beText}</p>

      {voucher > 0 && (
        <p className="text-center text-gold-dark text-[11px] mt-3">Includes the £4 delivery voucher you earn by spending £300+ in a month.</p>
      )}
    </div>
  );
}
export default function Membership() {
  return (
    <div className="bg-cream text-ink min-h-screen">

      {/* HERO */}
      <section className="section-dark relative text-cream py-20 md:py-28 overflow-hidden" style={{background:'linear-gradient(135deg,#0F0F12 0%,#1a1208 40%,#2c1e08 70%,#0F0F12 100%)'}}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-15 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{background:'radial-gradient(ellipse at 70% 50%, rgba(201,169,97,0.12) 0%, transparent 60%)'}} />
        <div className="relative max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <span className="pill bg-gold text-ink text-[10px] mb-4 inline-block">SOUQLI MEMBER &middot; £10.99 / MONTH</span>
          <h1 className="display text-4xl md:text-5xl text-cream mb-4 leading-tight">Shop smarter.<br />Pay a lot less.</h1>
          <p className="text-cream/65 text-base md:text-lg max-w-[52ch] mx-auto mb-8 leading-relaxed">
            For £10.99 a month you pay no buyer protection fee on up to 12 orders and £300 of spend, get a £4 delivery voucher on big orders,
            and see new drops before anyone else.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <JoinCTA plan="monthly" className="btn btn-gold text-sm px-10 py-3.5" label="Start membership →" />
            <a href="#compare" className="btn btn-outline-cream text-sm px-8 py-3.5">See what is included</a>
          </div>
          <p className="text-cream/40 text-xs mt-4">No commitment. Cancel any time.</p>
        </div>
      </section>

      {/* PERKS GRID */}
      <section id="compare" className="py-16 border-t border-warmline bg-cream">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl md:text-3xl text-ink text-center mb-2">Everything you get</h2>
          <p className="text-mute text-sm text-center mb-10">Five reasons members do not go back to guest.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {perks.map(p => (
              <div key={p.title} className="bg-white border border-warmline hover:border-gold/60 hover:shadow-luxe rounded-luxe p-6 transition-all">
                <IconMark name={p.icon} />
                <h3 className="display text-base text-ink mb-2">{p.title}</h3>
                <p className="text-mute text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* gold rule divider */}
      <div className="h-1" style={{background:'linear-gradient(90deg,transparent,#D98A2B 30%,#A66018 70%,transparent)'}} />

      {/* FEE TABLE - highlighted Guest vs Member */}
      <section className="py-16 bg-cream-deep">
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          <h2 className="display text-2xl text-ink text-center mb-2">Guest vs Member</h2>
          <p className="text-mute text-sm text-center mb-10">Everything a guest gets, plus the member-only extras that pay for themselves.</p>
          <div className="overflow-x-auto">
            <div className="min-w-[520px] grid grid-cols-[1.5fr_1fr_1fr] rounded-luxe overflow-hidden shadow-frame border border-warmline">
              {/* Header row */}
              <div className="section-dark bg-ink text-cream px-5 py-4 stamp text-[10px] flex items-end">Feature</div>
              <div className="section-dark bg-ink text-cream px-4 py-4 text-center">
                <div className="stamp text-[10px] text-cream/70">Guest</div>
                <div className="text-[11px] text-cream/50 mt-1">Free</div>
              </div>
              <div className="section-dark bg-ink text-cream px-4 py-4 text-center border-l border-gold/40 relative">
                <span className="inline-block px-2 py-0.5 bg-gold text-ink text-[8px] font-bold rounded mb-1">BEST VALUE</span>
                <div className="stamp text-[10px] text-gold">Member</div>
                <div className="text-[11px] text-cream/50 mt-0.5">£10.99 / mo</div>
              </div>
              {/* Comparison rows */}
              {compareRows.map((r, i) => (
                <Fragment key={r.label}>
                  <div className={`px-5 py-3.5 text-[13px] text-ink/85 border-t border-warmline ${i % 2 ? 'bg-cream' : 'bg-white'}`}>{r.label}</div>
                  <div className={`px-4 py-3.5 text-center text-[13px] border-t border-warmline ${i % 2 ? 'bg-cream' : 'bg-white'}`}>{cmpCell(r.guest, false)}</div>
                  <div className="px-4 py-3.5 text-center text-[13px] border-t border-l border-gold/25 bg-gold/8">{cmpCell(r.member, true)}</div>
                </Fragment>
              ))}
            </div>
          </div>
          <div className="text-center mt-6">
            <JoinCTA plan="monthly" className="btn btn-gold text-sm px-9 py-3" label="Become a member →" />
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-16 bg-cream relative overflow-hidden">
        <div className="absolute inset-0 dotgrid-light bg-[length:22px_22px] opacity-[0.04] pointer-events-none" />
        <div className="max-w-[640px] mx-auto px-6 md:px-10"><BreakevenCalc /></div>
      </section>

      {/* PRICING CARD */}
      <section className="py-16 border-t border-warmline bg-cream-deep">
        <div className="max-w-[480px] mx-auto px-6 md:px-10 text-center">
          <div className="rounded-luxe border border-gold/40 bg-white p-8 shadow-frame relative overflow-hidden">
            <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-5 pointer-events-none" />
            <div className="relative">
              <span className="pill bg-gold text-ink text-[9px] mb-4 inline-block">SOUQLI MEMBER</span>
              <div className="mb-2">
                <span className="display text-5xl text-gold">£10.99</span>
                <span className="text-mute text-sm"> / month</span>
              </div>
              <ul className="text-left space-y-3 mb-8">
                {['Fee-free on 12 orders / £300 a month (then 3.5% + 60p)', '£4 delivery voucher when you spend £300+', '1-hour early access to new drops', 'Verified Buyer badge on your profile', 'Priority human support'].map(item => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-ink/80">
                    <span className="text-gold mt-0.5">✓</span>{item}
                  </li>
                ))}
              </ul>
              <JoinCTA plan="monthly" className="btn btn-gold w-full text-sm py-3.5 text-center block" label="Become a member →" />
              <p className="text-[11px] text-mute mt-3">Billed monthly. Cancel any time. No hidden fees.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 border-t border-warmline bg-cream">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <div className="stamp text-gold-dark text-[9px] mb-2">GOOD TO KNOW</div>
            <h2 className="display text-3xl text-ink">Membership, answered.</h2>
            <p className="text-mute text-sm mt-2">Everything about fees, vouchers and cancelling - in plain English.</p>
          </div>
          <div className="space-y-3">
            {[
              { q: 'When does my membership start?',             a: 'Immediately after payment. Your fee-free allowance, delivery voucher, and early access all activate straight away.' },
              { q: 'How do the fee-free limits work?',           a: 'As an active member you pay no buyer protection fee on your first 12 orders and up to £300 of spend each month. Beyond either limit, a 3.5% + 60p fee applies, while guests always pay 4.5% + 70p. Limits reset at the start of every month.' },
              { q: 'How does the £4 delivery voucher work?',     a: 'Spend over £300 in a month and you earn a £4 voucher (once per month). Apply it to the delivery on any later order; it is optional, and any unused balance expires after three months.' },
              { q: 'Do members really get early access?',        a: 'Yes. You see newly listed pieces a full hour before non-members, so you get first pick of the best drops before they sell out.' },
              { q: 'Can I use my voucher and fee-free allowance together?', a: 'Absolutely, they stack. Shop fee-free within your monthly limits and still apply your £4 delivery voucher on a later order.' },
              { q: 'Can I cancel any time?',                     a: 'Yes. Cancel from your dashboard with one click. You keep every benefit until the end of the billing period you have already paid for.' },
              { q: 'Is my payment secure?',                      a: 'Payments are processed by Stripe - the same infrastructure trusted by Apple, Amazon and Google. Souqli never sees or stores your card details.' },
            ].map(({ q, a }) => (
              <details key={q} className="group bg-white border border-warmline rounded-luxe px-5 py-4 open:shadow-luxe transition-all">
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <span className="text-ink font-medium text-sm md:text-[15px]">{q}</span>
                  <span className="shrink-0 w-6 h-6 rounded-full border border-warmline grid place-items-center text-mute text-lg leading-none group-open:bg-gold group-open:text-ink group-open:border-gold group-open:rotate-45 transition-all duration-200">+</span>
                </summary>
                <p className="text-mute text-sm leading-relaxed mt-3">{a}</p>
              </details>
            ))}
          </div>
          <p className="text-center text-sm text-mute mt-8">Still curious? <Link to="/contact" className="text-gold-dark underline underline-offset-2 hover:text-gold">Ask our team →</Link></p>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="section-dark py-20 text-cream relative overflow-hidden" style={{background:'linear-gradient(135deg,#0F0F12 0%,#1a1208 50%,#0F0F12 100%)'}}>
        <div className="absolute inset-0 dotgrid-light bg-[length:18px_18px] opacity-10 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{background:'radial-gradient(ellipse at 30% 50%, rgba(201,169,97,0.10) 0%, transparent 60%)'}} />
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <h2 className="display text-3xl text-cream mb-3">Ready to pay less?</h2>
          <p className="text-cream/55 text-sm mb-8">Join thousands of members already saving on every order.</p>
          <JoinCTA plan="monthly" className="btn btn-gold text-sm px-12 py-4" label="Start membership for £10.99/mo →" />
          <p className="text-cream/30 text-xs mt-4">No commitment · Cancel any time · Payments by Stripe</p>
        </div>
      </section>

    </div>
  );
}
